import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { fetchEventById, fetchVendorById, fetchTicketsByEventId } from '../supabaseClient';
import Layout from '../components/Layout';
import { MdLocationOn } from 'react-icons/md';
import { FaBan, FaCalendarAlt } from 'react-icons/fa';
import Spinner from '../components/common/Spinner';

const SPORTS_EVENTS = [
  'Basketball',
  'Football',
  'Soccer',
  'Baseball',
  'Softball',
  'Volleyball',
  'Track & Field',
  'Wrestling',
  'Tennis',
  'Golf',
  'Swimming',
  'Cross Country',
];

const defaultSportsImageUrl = "https://s3.amazonaws.com/com.ticketspicket.app/activity-images/basketball_Edited.jpg";
const defaultEventImageUrl = "https://www.pncfairfaxconnection.com/wp-content/themes/pnc-child/img/event_placeholder.jpg";


const areTicketsAvailable = (event) => {
  if (!event.tickets_sale_start_date) return true; 
  const now = new Date();
  const saleStartDate = new Date(event.tickets_sale_start_date);
  return now >= saleStartDate;
};


const formatTicketSaleDate = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    weekday: 'short',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });
};


const richTextStyles = `
  .prose ul {
    list-style-type: disc;
    padding-left: 1.5em;
    margin: 1em 0;
  }
  .prose ol {
    list-style-type: decimal;
    padding-left: 1.5em;
    margin: 1em 0;
  }
  .prose p {
    margin-bottom: 1em;
  }
  .prose a {
    color: #2563eb;
    text-decoration: underline;
  }
  .prose h1 {
    font-size: 1.5em;
    font-weight: bold;
    margin: 1em 0;
  }
  .prose h2 {
    font-size: 1.25em;
    font-weight: bold;
    margin: 1em 0;
  }
  .prose strong {
    font-weight: bold;
  }
  .prose em {
    font-style: italic;
  }
`;

function EventPage() {
  const [event, setEvent] = useState(null);
  const [vendor, setVendor] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [ticketQuantities, setTicketQuantities] = useState({});
  const { id } = useParams();
  const navigate = useNavigate(); // Initialize navigate

  useEffect(() => {
    const fetchEventData = async () => {
      const { data: eventData, error } = await fetchEventById(id);
      
      if (error || !eventData) {
        navigate('/404');
        return;
      }

      setEvent(eventData);

      const { data: vendorData, error: vendorError } = await fetchVendorById(eventData.vendor_id);
      if (vendorError) {
        console.error(vendorError);
        return;
      }
      setVendor(vendorData);

      const { data: ticketData, error: ticketError } = await fetchTicketsByEventId(id);
      if (ticketError) {
        console.error(ticketError);
        return;
      }
      setTickets(ticketData || []);
      setTicketQuantities(ticketData.reduce((acc, ticket) => ({ ...acc, [ticket.id]: 0 }), {}));
    };

    fetchEventData();
  }, [id, navigate]);

  const openModal = () => setIsModalOpen(true);
  const closeModal = () => setIsModalOpen(false);

  const increaseQuantity = (ticketId) => {
    setTicketQuantities({ ...ticketQuantities, [ticketId]: ticketQuantities[ticketId] + 1 });
  };

  const decreaseQuantity = (ticketId) => {
    if (ticketQuantities[ticketId] > 0) {
      setTicketQuantities({ ...ticketQuantities, [ticketId]: ticketQuantities[ticketId] - 1 });
    }
  };

  const calculateTotalAmount = () => {
    return tickets.reduce((total, ticket) => total + ticket.price * (ticketQuantities[ticket.id] || 0), 0).toFixed(2);
  };

  const handleCheckout = () => {
    closeModal();
    
    // Calculate number of paid tickets
    const paidTicketsCount = tickets.reduce((total, ticket) => {
      if (ticket.price > 0) {
        return total + (ticketQuantities[ticket.id] || 0);
      }
      return total;
    }, 0);

    navigate('/checkout', {
      state: {
        event,
        vendor,
        tickets,
        ticketQuantities,
        totalAmount: calculateTotalAmount(),
        paidTicketsCount
      },
    });
  };
  

  if (!event || !vendor) return <Spinner />;
  
  const isCancelled = event.isCancelled || event.is_cancelled;
  
  const ticketsAvailable = areTicketsAvailable(event);

  const createMarkup = (htmlContent) => {
    return { __html: htmlContent };
  };

  return (
    <Layout vendorData={vendor}>
      <style>{richTextStyles}</style>
      <div className="min-h-screen bg-gray-100">
        <section
          className="relative text-white p-8"
          style={{
            backgroundImage: `url(${
              event.default_image?.image_path || 
              (SPORTS_EVENTS.includes(event.event_type) ? defaultSportsImageUrl : defaultEventImageUrl)
            })`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        >
          <div className="absolute inset-0 bg-black bg-opacity-50"></div>
          
          {/* Cancel Banner */}
          {isCancelled && (
            <div className="absolute top-0 left-0 right-0 bg-red-600 text-white py-3 px-4 z-20">
              <div className="max-w-6xl mx-auto flex items-center justify-center">
                <FaBan className="mr-3 text-xl" />
                <span className="font-bold text-lg">
                  EVENT CANCELLED - Cancelled on {new Date(event.cancelled_on_date || event.cancelledOnDate).toLocaleDateString()}
                </span>
              </div>
            </div>
          )}
          
          {/* Ticket Sale Banner */}
          {!isCancelled && !ticketsAvailable && (
            <div className="absolute top-0 left-0 right-0 bg-orange-600 text-white py-3 px-4 z-20">
              <div className="max-w-6xl mx-auto flex items-center justify-center">
                <FaCalendarAlt className="mr-3 text-xl" />
                <span className="font-bold text-lg">
                  TICKETS AVAILABLE {formatTicketSaleDate(event.tickets_sale_start_date).toUpperCase()}
                </span>
              </div>
            </div>
          )}
          
          <div className={`relative z-10 flex flex-col md:flex-row items-start md:items-center justify-between md:p-12 max-w-6xl mx-auto ${isCancelled || !ticketsAvailable ? 'mt-16' : ''}`}>
            <div className="flex flex-col">
              <h1 className={`text-3xl font-bold ${isCancelled ? 'line-through text-gray-300' : ''}`}>
                {SPORTS_EVENTS.includes(event.event_type) && vendor
                  ? `${vendor.short_name} vs ${event.opponent}`
                  : event.title
                }
              </h1>
              {isCancelled && (
                <div className="flex items-center mt-2 text-red-300">
                  <FaBan className="mr-2" />
                  <span className="text-lg font-semibold">This event has been cancelled</span>
                </div>
              )}
              {!isCancelled && !ticketsAvailable && (
                <div className="flex items-center mt-2 text-orange-300">
                  <FaCalendarAlt className="mr-2" />
                  <span className="text-lg font-semibold">Tickets will be available {formatTicketSaleDate(event.tickets_sale_start_date)}</span>
                </div>
              )}
              <h2 className={`text-2xl font-semibold ${isCancelled ? 'text-gray-300' : ''}`}>
                {SPORTS_EVENTS.includes(event.event_type)
                  ? `${event.gender_type} ${event.event_type}`
                  : event.event_type
                }
              </h2>
              <div className={`flex items-center mt-2 text-lg ${isCancelled ? 'text-gray-300' : ''}`}>
                <MdLocationOn className="mr-1" />
                <span>{event.location}</span>
              </div>
            </div>
            
            <div
              className={`text-white text-center p-3 rounded-lg shadow-lg mt-4 md:mt-0 ${isCancelled ? 'opacity-60' : ''}`}
              style={{
                backgroundColor: isCancelled ? '#dc2626' : (vendor?.primary_color || '#0000FF'), 
                minWidth: '80px'
              }}
            >
              {isCancelled && (
                <div className="mb-2">
                  <FaBan className="text-2xl mx-auto" />
                </div>
              )}
              <span className="block text-sm font-semibold">
                {new Date(event.start_date).toLocaleString('en-US', { 
                  weekday: 'short',
                })}
              </span>
              <span className="block text-2xl font-bold">
                {new Date(event.start_date).toLocaleString('en-US', { 
                  day: '2-digit',
                })}
              </span>
              <span className="block text-sm font-semibold">
                {new Date(event.start_date).toLocaleString('en-US', { 
                  month: 'short',
                  
                })}
              </span>
              <span className="block text-sm font-semibold mt-1">
                {new Date(event.start_date).toLocaleString('en-US', { 
                  hour: '2-digit', 
                  minute: '2-digit',
                  hour12: true,
                
                })}
              </span>
            </div>
          </div>
        </section>

        {!SPORTS_EVENTS.includes(event.event_type) && event.description && (
          <section className="py-8 px-4">
            <div className={`max-w-6xl mx-auto bg-white rounded-lg shadow-lg p-6 ${isCancelled || !ticketsAvailable ? 'opacity-75' : ''}`}>
              <h2 className="text-2xl font-bold text-gray-800 mb-4">About This Event</h2>
              {isCancelled && (
                <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex items-center text-red-700">
                    <FaBan className="mr-2" />
                    <span className="font-semibold">This event has been cancelled and tickets are no longer available for purchase.</span>
                  </div>
                </div>
              )}
              {!isCancelled && !ticketsAvailable && (
                <div className="mb-4 p-4 bg-orange-50 border border-orange-200 rounded-lg">
                  <div className="flex items-center text-orange-700">
                    <FaCalendarAlt className="mr-2" />
                    <span className="font-semibold">Tickets will be available for purchase starting {formatTicketSaleDate(event.tickets_sale_start_date)}.</span>
                  </div>
                </div>
              )}
              <div 
                className={`prose max-w-none text-gray-600 ${isCancelled ? 'line-through' : ''}`}
                dangerouslySetInnerHTML={createMarkup(event.description)}
              />
            </div>
          </section>
        )}

        <section className="p-6">
          <h2 className="text-2xl font-bold mb-4">Available Tickets & Prices</h2>
          <div className={`bg-white p-6 shadow-lg rounded-lg ${isCancelled || !ticketsAvailable ? 'opacity-75' : ''}`}>
            {isCancelled && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-center justify-center text-red-700">
                  <FaBan className="mr-3 text-xl" />
                  <div className="text-center">
                    <p className="font-semibold text-lg">Event Cancelled</p>
                    <p className="text-sm">Tickets are no longer available for this event.</p>
                  </div>
                </div>
              </div>
            )}
            {!isCancelled && !ticketsAvailable && (
              <div className="mb-6 p-4 bg-orange-50 border border-orange-200 rounded-lg">
                <div className="flex items-center justify-center text-orange-700">
                  <FaCalendarAlt className="mr-3 text-xl" />
                  <div className="text-center">
                    <p className="font-semibold text-lg">Tickets Not Available Yet</p>
                    <p className="text-sm">Tickets will be available starting {formatTicketSaleDate(event.tickets_sale_start_date)}.</p>
                  </div>
                </div>
              </div>
            )}
            
            {tickets.length > 0 ? (
              <>
                <ul className="divide-y divide-gray-200">
                  {tickets.map(ticket => (
                    <li key={ticket.id} className={`py-4 flex justify-between items-center ${isCancelled || !ticketsAvailable ? 'text-gray-500' : ''}`}>
                      <span className={isCancelled ? 'line-through' : ''}>{ticket.name}</span>
                      <span className={isCancelled ? 'line-through' : ''}>${ticket.price}</span>
                    </li>
                  ))}
                </ul>
                <button
                  onClick={isCancelled || !ticketsAvailable ? undefined : openModal}
                  disabled={isCancelled || !ticketsAvailable}
                  className={`mt-4 w-full px-4 py-2 rounded-lg transition-colors duration-200 ${
                    isCancelled 
                      ? 'bg-gray-400 text-gray-600 cursor-not-allowed'
                      : !ticketsAvailable
                      ? 'bg-orange-400 text-orange-800 cursor-not-allowed'
                      : 'bg-blue-600 text-white hover:bg-blue-700'
                  }`}
                >
                  {isCancelled 
                    ? 'Event Cancelled - Tickets Unavailable'
                    : !ticketsAvailable
                    ? `Tickets Available ${formatTicketSaleDate(event.tickets_sale_start_date)}`
                    : 'Buy Tickets'}
                </button>
              </>
            ) : (
              <p className="text-center text-gray-500">
                {isCancelled 
                  ? 'This event has been cancelled' 
                  : !ticketsAvailable
                  ? `Tickets will be available starting ${formatTicketSaleDate(event.tickets_sale_start_date)}`
                  : 'No tickets available as of now'}
              </p>
            )}
          </div>
        </section>

        {isModalOpen && !isCancelled && ticketsAvailable && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white p-6 rounded-lg shadow-lg w-full max-w-3xl relative">
              <button onClick={closeModal} className="absolute top-3 right-3 text-gray-600 text-2xl font-bold">
                &times;
              </button>

              {/* Vendor Logo Section */}
              <div className="flex justify-center items-center mb-6">
                {vendor?.logoURL ? (
                  <img src={vendor.logoURL} alt={`${vendor.company_name} Logo`} className="h-16 w-16 object-contain" />
                ) : (
                  <div className="h-16 w-16 bg-gray-300 rounded-full flex items-center justify-center">
                    <span className="text-gray-600 text-lg">Logo</span>
                  </div>
                )}
              </div>

              <div className="flex flex-col md:flex-row justify-between">
                {/* Left Section - Choose Your Tickets */}
                <div className="w-full md:w-1/2 p-4 border-b md:border-b-0 md:border-r">
                  <h3 className="text-xl font-bold mb-4">Choose Your Tickets</h3>
                  {tickets.map(ticket => (
                    <div key={ticket.id} className="flex items-center justify-between mb-4">
                      <span className="font-semibold">{ticket.name}</span>
                      <span className="text-gray-600">${ticket.price}</span>
                      <div className="flex items-center">
                        <button onClick={() => decreaseQuantity(ticket.id)} className="px-2 py-1 bg-gray-300 rounded-l">-</button>
                        <span className="px-4 py-1 border">{ticketQuantities[ticket.id]}</span>
                        <button onClick={() => increaseQuantity(ticket.id)} className="px-2 py-1 bg-gray-300 rounded-r">+</button>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Right Section - Selection Summary */}
                <div className="w-full md:w-1/2 p-4">
                  <h3 className="text-xl font-bold mb-4">Selection Summary</h3>
                  {tickets.map(ticket => (
                    <div key={ticket.id} className="flex justify-between mb-2">
                      <span>{ticket.name} x {ticketQuantities[ticket.id]}</span>
                      <span>${(ticket.price * ticketQuantities[ticket.id]).toFixed(2)}</span>
                    </div>
                  ))}
                  <div className="border-t pt-4 mt-4">
                    <h4 className="font-bold text-lg">Total Amount: ${calculateTotalAmount()}</h4>
                    <button onClick={handleCheckout} disabled={Object.values(ticketQuantities).reduce((a, b) => a + b, 0) === 0} className="mt-4 w-full px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 ">
                      Checkout
                    </button>
                  </div>
                </div>
              </div>

              <button onClick={closeModal} className="mt-4 w-full px-4 py-2 bg-gray-500 text-white rounded-lg">Done</button>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}

export default EventPage;
